import type { ProcessorCreatePayload } from "@/customTypings/processor/Processor";
import { authHeader, handleResponse } from "..";

export const PROCESSOR_CREATE_CONFLICT = {
    duplicateName: "processor.duplicateName",
    duplicatePermit: "processor.duplicatePermit",
    deletedRestoreAvailable: "processor.deletedRestoreAvailable",
    duplicateAmbiguous: "processor.duplicateAmbiguous",
} as const;

export type ProcessorDeletedRestoreAvailableConflict = {
    code: string;
    message: string;
    existingId: string;
    existingName: string;
};

export function isProcessorDeletedRestoreAvailableConflict(
    value: unknown,
    expectedCode: string
): value is ProcessorDeletedRestoreAvailableConflict {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as ProcessorDeletedRestoreAvailableConflict).code === expectedCode &&
        typeof (value as ProcessorDeletedRestoreAvailableConflict).existingId === "string"
    );
}

export class ProcessorCreateConflictError extends Error {
    readonly conflict: ProcessorDeletedRestoreAvailableConflict;

    constructor(conflict: ProcessorDeletedRestoreAvailableConflict) {
        super(conflict.message);
        this.name = "ProcessorCreateConflictError";
        this.conflict = conflict;
    }
}

function siteProcessorsBase(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/processors`;
}

export async function postProcessorCreate<TDetail>(
    siteId: string,
    payload: ProcessorCreatePayload
): Promise<TDetail> {
    const response = await fetch(`${siteProcessorsBase(siteId)}/create`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    });
    const text = await response.text();
    let data: unknown = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        /* non-JSON */
    }

    if (
        response.status === 409 &&
        isProcessorDeletedRestoreAvailableConflict(data, PROCESSOR_CREATE_CONFLICT.deletedRestoreAvailable)
    ) {
        throw new ProcessorCreateConflictError(data);
    }

    if (!response.ok) {
        const err = data as { code?: string; message?: string } | null;
        if (err?.code === PROCESSOR_CREATE_CONFLICT.duplicateName) {
            throw new Error(err.message ?? "A Processor with this name already exists at this site.");
        }
        if (err?.code === PROCESSOR_CREATE_CONFLICT.duplicatePermit) {
            throw new Error(err.message ?? "A Processor with this environmental permit already exists at this site.");
        }
        if (err?.code === PROCESSOR_CREATE_CONFLICT.duplicateAmbiguous) {
            throw new Error(err.message ?? "Multiple deleted Processors match these details.");
        }
    }

    return handleResponse({
        text: async () => text,
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
    }) as Promise<TDetail>;
}
